import { useSyncExternalStore } from "react";
import type { RoadAssistCoverageAnswers, RoadAssistTier } from "./road-assist-offers";
import type { RoadAssistOffer, RoadAssistPaymentLink } from "./road-assist-checkout";

const STORAGE_KEY = "roadAssist-checkout-v1";
const CHANGE_EVENT = "roadAssist-checkout-state-change";

export type RoadAssistCheckoutEnvelope = {
  carId: string;
  startDate: string | null;            // yyyy-MM-dd
  periodMonths: 3 | 6 | 12 | null;
  tier: RoadAssistTier | null;         // step-1 global coverage tier
  coverage: RoadAssistCoverageAnswers | null;
  orderHash: string | null;
  chosenOffer: RoadAssistOffer | null;
  paymentLink: RoadAssistPaymentLink | null;
  pendingOrderId: string | null;       // = paymentLink.orderId (mirror doc id)
  // Set when the client ticks "Am citit și accept documentul ofertei și confirm că
  // datele sunt corecte" on step 4. Unlike the other products the offer is selected
  // one screen later (/plata), so the tick has to travel with the envelope for the
  // declaration record to be written against it.
  dataConfirmed: boolean;
};

function emptyEnvelope(carId: string): RoadAssistCheckoutEnvelope {
  return {
    carId, startDate: null, periodMonths: null, tier: null, coverage: null,
    orderHash: null, chosenOffer: null, paymentLink: null, pendingOrderId: null,
    dataConfirmed: false,
  };
}

function readRaw(): RoadAssistCheckoutEnvelope | null {
  if (typeof window === "undefined") return null;
  try {
    const json = window.sessionStorage.getItem(STORAGE_KEY);
    return json ? (JSON.parse(json) as RoadAssistCheckoutEnvelope) : null;
  } catch { return null; }
}

function writeRaw(env: RoadAssistCheckoutEnvelope | null): void {
  if (typeof window === "undefined") return;
  if (env == null) window.sessionStorage.removeItem(STORAGE_KEY);
  else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(env));
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CHANGE_EVENT, cb);
  window.addEventListener("storage", cb);
  return () => { window.removeEventListener(CHANGE_EVENT, cb); window.removeEventListener("storage", cb); };
}
function getSnapshot(): string {
  if (typeof window === "undefined") return "";
  return window.sessionStorage.getItem(STORAGE_KEY) ?? "";
}
function getServerSnapshot(): string { return ""; }

export type UseRoadAssistCheckoutState = {
  state: RoadAssistCheckoutEnvelope;
  update: (patch: Partial<RoadAssistCheckoutEnvelope>) => void;
  clear: () => void;
};

export function useRoadAssistCheckoutState(carId: string): UseRoadAssistCheckoutState {
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const raw = readRaw();
  const state =
    raw && raw.carId === carId
      ? raw
      : (() => { const fresh = emptyEnvelope(carId); writeRaw(fresh); return fresh; })();
  return {
    state,
    update: (patch) => writeRaw({ ...state, ...patch }),
    clear: () => writeRaw(null),
  };
}

export function peekRoadAssistCheckoutState(): RoadAssistCheckoutEnvelope | null { return readRaw(); }
export function clearRoadAssistCheckoutState(): void { writeRaw(null); }
export function writeRoadAssistCheckoutState(env: RoadAssistCheckoutEnvelope): void { writeRaw(env); }
